import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Download, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Clock,
  Target,
  Shield,
  Coins,
  Activity,
  Calendar,
  AlertTriangle
} from "lucide-react";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mtf-analysis`;

// Helper to format numbers
const formatPnL = (value) => {
  if (value === undefined || value === null) return "-";
  const num = parseFloat(value);
  const formatted = num >= 0 ? `+$${num.toFixed(2)}` : `-$${Math.abs(num).toFixed(2)}`;
  return formatted;
};

const formatPercent = (value) => {
  if (value === undefined || value === null) return "-";
  return `${value}%`;
};

// Color coding based on win rate and PnL
const getWinRateColor = (winRate) => {
  if (winRate === undefined || winRate === null) return "text-gray-400";
  if (winRate >= 40) return "text-green-400";
  if (winRate >= 30) return "text-yellow-400";
  return "text-red-400";
};

const getPnLColor = (pnl) => {
  if (pnl === undefined || pnl === null) return "text-gray-400";
  if (pnl > 0) return "text-green-400";
  if (pnl < 0) return "text-red-400";
  return "text-gray-400";
};

// Stat Card Component
const StatCard = ({ title, value, subValue, icon: Icon, color = "text-primary" }) => (
  <div className="glass-card p-4 rounded-xl border border-white/10" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      {Icon && <Icon className={`w-8 h-8 ${color}`} />}
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
      </div>
    </div>
  </div>
);

// Breakdown Table Component
const BreakdownTable = ({ title, data, icon: Icon }) => {
  const [expanded, setExpanded] = useState(true);
  
  if (!data || Object.keys(data).length === 0) return null;
  
  const entries = Object.entries(data).sort((a, b) => (b[1].trades || 0) - (a[1].trades || 0));
  
  return (
    <div className="glass-card rounded-xl border border-white/10 overflow-hidden" data-testid={`breakdown-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-primary" />}
          <h3 className="font-semibold">{title}</h3>
          <span className="text-xs text-muted-foreground">({entries.length})</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-right p-3 font-medium">Trades</th>
                <th className="text-right p-3 font-medium">W/L</th>
                <th className="text-right p-3 font-medium">Win Rate</th>
                <th className="text-right p-3 font-medium">PnL</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([key, val]) => (
                <tr key={key} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-3 font-medium">{key}</td>
                  <td className="text-right p-3">{val.trades || 0}</td>
                  <td className="text-right p-3">{val.wins || 0}W/{val.losses || 0}L</td>
                  <td className={`text-right p-3 font-medium ${getWinRateColor(val.win_rate)}`}>
                    {formatPercent(val.win_rate)}
                  </td>
                  <td className={`text-right p-3 font-medium ${getPnLColor(val.pnl)}`}>
                    {formatPnL(val.pnl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Guard Scenarios Table
const GuardScenariosTable = ({ data }) => {
  const [expanded, setExpanded] = useState(true);

  if (!data || Object.keys(data).length === 0) return null;

  // Production guard cascade order. Category label is rendered as a small badge
  // next to each row so SKIP vs WAIT guards are visually distinguishable.
  const scenarios = [
    // ---- Live SKIP guards ----
    { key: "v203_bearish_buy_veto",         label: "V2.0.3: Bearish 5m Momentum BUY Veto",   category: "Live · SKIP" },
    { key: "v204_top_range_buy",            label: "V2.0.4: Top-of-Range BUY Guard",          category: "Live · SKIP" },
    { key: "v205_bullish_sell_veto",        label: "V2.0.5: Bullish 5m Momentum SELL Veto",  category: "Live · SKIP" },
    { key: "v206_bottom_range_sell_veto",   label: "V2.0.6: Bottom-of-Range Oversold SELL Veto (revised)", category: "Live · SKIP" },
    { key: "all_live_skip_guards_combined", label: "All Live SKIP Guards Combined (V2.0.3 + 4 + 5 + 6)",   category: "Live · SKIP" },
    // ---- Live WAIT / always-on ----
    { key: "v201_range_momentum_rsi_block", label: "V2.0.1: 5m Range + Momentum + RSI Block", category: "Live · WAIT" },
    { key: "v202_sideways_chop_buy",        label: "V2.0.2: Sideways Chop BUY Guard",         category: "Live · WAIT" },
    // ---- Soft WAIT / shadow-first ----
    { key: "v207_low_conf_sell_gate",       label: "V2.0.7: Low-Confidence Bear High-Vol SELL Gate (shadow)", category: "Soft · WAIT" },
    { key: "v208_buy_late_chase",           label: "V2.0.8: BUY Late-Chase RSI/Range",        category: "Soft · WAIT" },
    { key: "v209_sell_oversold",            label: "V2.0.9: SELL Oversold Exhaustion (RSI<30)", category: "Soft · WAIT" },
    { key: "v210_buy_bad_volatility",       label: "V2.0.10: BUY Bad-Volatility RSI",         category: "Soft · WAIT" },
    { key: "v211_bear_buy_momentum",        label: "V2.0.11: Bear-Market BUY Momentum",       category: "Soft · WAIT" },
    { key: "v212_buy_weak_sideways",        label: "V2.0.12: BUY Weak-Sideways Diagnostic (revised)", category: "Soft · WAIT" },
    { key: "all_wait_guards_combined",      label: "All WAIT Guards Combined (V2.0.1 + 2 + 8 + 9 + 10 + 11 + 12)", category: "Soft · WAIT" },
  ];

  const categoryBadgeClass = (category) => {
    if (category.includes("SKIP")) return "bg-red-500/20 text-red-300";
    if (category.includes("Soft")) return "bg-yellow-500/20 text-yellow-300";
    return "bg-blue-500/20 text-blue-300";
  };

  return (
    <div className="glass-card rounded-xl border border-white/10 overflow-hidden" data-testid="guard-scenarios">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-yellow-400" />
          <h3 className="font-semibold">Guard Scenarios</h3>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-medium">Category</th>
                <th className="text-left p-3 font-medium">Scenario</th>
                <th className="text-right p-3 font-medium">Blocked</th>
                <th className="text-right p-3 font-medium">Blocked WR</th>
                <th className="text-right p-3 font-medium">Blocked PnL</th>
                <th className="text-right p-3 font-medium">Remaining</th>
                <th className="text-right p-3 font-medium">Remaining WR</th>
                <th className="text-right p-3 font-medium">Remaining PnL</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map(({ key, label, category }) => {
                const scenario = data[key];
                if (!scenario) return null;
                const blocked = scenario.blocked || {};
                const remaining = scenario.remaining || {};
                return (
                  <tr key={key} className="border-t border-white/5 hover:bg-white/5" data-testid={`guard-row-${key}`}>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${categoryBadgeClass(category)}`}>
                        {category}
                      </span>
                    </td>
                    <td className="p-3 font-medium">{label}</td>
                    <td className="text-right p-3">{blocked.trades || 0}</td>
                    <td className={`text-right p-3 ${getWinRateColor(blocked.win_rate)}`}>
                      {formatPercent(blocked.win_rate)}
                    </td>
                    <td className={`text-right p-3 ${getPnLColor(blocked.pnl)}`}>
                      {formatPnL(blocked.pnl)}
                    </td>
                    <td className="text-right p-3">{remaining.trades || 0}</td>
                    <td className={`text-right p-3 ${getWinRateColor(remaining.win_rate)}`}>
                      {formatPercent(remaining.win_rate)}
                    </td>
                    <td className={`text-right p-3 ${getPnLColor(remaining.pnl)}`}>
                      {formatPnL(remaining.pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Combo Analysis Table
const ComboAnalysisTable = ({ data }) => {
  const [expanded, setExpanded] = useState(true);
  
  if (!data || data.length === 0) return null;
  
  return (
    <div className="glass-card rounded-xl border border-white/10 overflow-hidden" data-testid="combo-analysis">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-400" />
          <h3 className="font-semibold">Top Combos (Market + Signal + Hourly + Momentum + Range)</h3>
          <span className="text-xs text-muted-foreground">({data.length})</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-medium">#</th>
                <th className="text-left p-3 font-medium">Market</th>
                <th className="text-left p-3 font-medium">Signal</th>
                <th className="text-left p-3 font-medium">Hourly</th>
                <th className="text-left p-3 font-medium">Momentum</th>
                <th className="text-left p-3 font-medium">Range</th>
                <th className="text-right p-3 font-medium">Trades</th>
                <th className="text-right p-3 font-medium">Win Rate</th>
                <th className="text-right p-3 font-medium">PnL</th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 15).map((combo, idx) => (
                <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-3 text-muted-foreground">{idx + 1}</td>
                  <td className="p-3">{combo.market_trend || '-'}</td>
                  <td className="p-3">{combo.signal || '-'}</td>
                  <td className="p-3">{combo.hourly}</td>
                  <td className="p-3">{combo.momentum}</td>
                  <td className="p-3">{combo.range_bucket}</td>
                  <td className="text-right p-3">{combo.trades}</td>
                  <td className={`text-right p-3 font-medium ${getWinRateColor(combo.win_rate)}`}>
                    {formatPercent(combo.win_rate)}
                  </td>
                  <td className={`text-right p-3 font-medium ${getPnLColor(combo.pnl)}`}>
                    {formatPnL(combo.pnl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Individual Trades Table (Expandable)
const TradesTable = ({ snapshotId }) => {
  const [expanded, setExpanded] = useState(false);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 25;
  
  const fetchTrades = useCallback(async () => {
    if (!snapshotId || !expanded) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/snapshot/${snapshotId}/trades`, {
        params: { page, limit }
      });
      setTrades(response.data.data || []);
      setTotalPages(response.data.total_pages || 0);
      setTotalCount(response.data.total_count || 0);
    } catch (err) {
      console.error("Error fetching trades:", err);
      toast.error("Failed to fetch trades");
    } finally {
      setLoading(false);
    }
  }, [snapshotId, expanded, page]);
  
  useEffect(() => {
    if (expanded) {
      fetchTrades();
    }
  }, [fetchTrades, expanded]);
  
  return (
    <div className="glass-card rounded-xl border border-white/10 overflow-hidden" data-testid="individual-trades">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold">Individual Trades</h3>
          {totalCount > 0 && <span className="text-xs text-muted-foreground">({totalCount})</span>}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div>
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">Loading trades...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-3 font-medium">Time</th>
                      <th className="text-left p-3 font-medium">Coin</th>
                      <th className="text-left p-3 font-medium">Signal</th>
                      <th className="text-left p-3 font-medium">Market</th>
                      <th className="text-right p-3 font-medium">Conf</th>
                      <th className="text-left p-3 font-medium">Momentum</th>
                      <th className="text-left p-3 font-medium">Hourly</th>
                      <th className="text-right p-3 font-medium">Range</th>
                      <th className="text-right p-3 font-medium">RSI</th>
                      <th className="text-left p-3 font-medium">Exit</th>
                      <th className="text-right p-3 font-medium">Hold</th>
                      <th className="text-right p-3 font-medium">PnL</th>
                      <th className="text-left p-3 font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map((trade, idx) => (
                      <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                        <td className="p-3 text-xs">
                          {trade.timestamp ? new Date(trade.timestamp).toLocaleString() : "-"}
                        </td>
                        <td className="p-3 font-medium">{trade.coin}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            trade.signal === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {trade.signal || '-'}
                          </span>
                        </td>
                        <td className="p-3 text-xs">{trade.market_trend || '-'}</td>
                        <td className="text-right p-3">{trade.confidence}</td>
                        <td className="p-3">{trade.momentum}</td>
                        <td className="p-3">{trade.hourly_trend}</td>
                        <td className="text-right p-3">{trade.range ? (trade.range * 100).toFixed(0) + '%' : '-'}</td>
                        <td className="text-right p-3">{trade.rsi?.toFixed(1) || '-'}</td>
                        <td className="p-3 text-xs">{trade.exit_reason}</td>
                        <td className="text-right p-3">{trade.holding_minutes?.toFixed(0)}m</td>
                        <td className={`text-right p-3 font-medium ${getPnLColor(trade.pnl)}`}>
                          {formatPnL(trade.pnl)}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            trade.result === 'correct' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {trade.result === 'correct' ? 'WIN' : 'LOSS'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-white/10">
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({totalCount} trades)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Snapshots History Table
const SnapshotsHistoryTable = ({ onSelectSnapshot, currentSnapshotId }) => {
  const [expanded, setExpanded] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 10;
  
  const fetchSnapshots = useCallback(async () => {
    if (!expanded) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/snapshots`, {
        params: { page, limit, sort_order: "desc" }
      });
      setSnapshots(response.data.data || []);
      setTotalPages(response.data.total_pages || 0);
    } catch (err) {
      console.error("Error fetching snapshots:", err);
    } finally {
      setLoading(false);
    }
  }, [expanded, page]);
  
  useEffect(() => {
    if (expanded) {
      fetchSnapshots();
    }
  }, [fetchSnapshots, expanded]);
  
  return (
    <div className="glass-card rounded-xl border border-white/10 overflow-hidden" data-testid="snapshots-history">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-400" />
          <h3 className="font-semibold">All Snapshots History</h3>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div>
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">Loading snapshots...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-3 font-medium">Snapshot Time</th>
                      <th className="text-left p-3 font-medium">Data Period</th>
                      <th className="text-right p-3 font-medium">Days</th>
                      <th className="text-right p-3 font-medium">Trades</th>
                      <th className="text-right p-3 font-medium">Win Rate</th>
                      <th className="text-right p-3 font-medium">PnL</th>
                      <th className="text-center p-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshots.map((s) => (
                      <tr 
                        key={s._id} 
                        className={`border-t border-white/5 hover:bg-white/5 ${
                          s._id === currentSnapshotId ? 'bg-primary/10' : ''
                        }`}
                      >
                        <td className="p-3 text-xs">
                          {s.snapshot_time ? new Date(s.snapshot_time).toLocaleString() : "-"}
                        </td>
                        <td className="p-3 text-xs">
                          {s.data_period?.start ? new Date(s.data_period.start).toLocaleDateString() : "-"} - {" "}
                          {s.data_period?.end ? new Date(s.data_period.end).toLocaleDateString() : "-"}
                        </td>
                        <td className="text-right p-3">{s.data_period?.days || 0}</td>
                        <td className="text-right p-3">{s.overall?.trades || 0}</td>
                        <td className={`text-right p-3 font-medium ${getWinRateColor(s.overall?.win_rate)}`}>
                          {formatPercent(s.overall?.win_rate)}
                        </td>
                        <td className={`text-right p-3 font-medium ${getPnLColor(s.overall?.pnl)}`}>
                          {formatPnL(s.overall?.pnl)}
                        </td>
                        <td className="text-center p-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSelectSnapshot(s._id)}
                            disabled={s._id === currentSnapshotId}
                          >
                            {s._id === currentSnapshotId ? "Current" : "View"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-white/10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Page {page} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const MtfAnalysisTab = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  const fetchLatestSnapshot = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/latest`);
      setSnapshot(response.data);
    } catch (err) {
      console.error("Error fetching snapshot:", err);
      setError(err.response?.data?.detail || "Failed to fetch MTF analysis data");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSnapshotById = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/snapshot/${id}`);
      setSnapshot(response.data);
      toast.success("Snapshot loaded");
    } catch (err) {
      console.error("Error fetching snapshot:", err);
      setError(err.response?.data?.detail || "Failed to fetch snapshot");
      toast.error("Failed to load snapshot");
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async () => {
    if (!snapshot?._id) return;
    
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/${snapshot._id}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mtf_snapshot_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export completed");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };
  
  useEffect(() => {
    fetchLatestSnapshot();
  }, []);
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="mtf-analysis-loading">
        <RefreshCw className="w-8 h-8 animate-spin mb-4 text-primary" />
        <p className="text-muted-foreground">Loading MTF Analysis...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="glass-card p-8 rounded-xl border border-red-500/30 text-center" data-testid="mtf-analysis-error">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <h3 className="text-lg font-semibold mb-2 text-red-400">Error Loading Data</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <p className="text-sm text-muted-foreground mb-4">
          Run <code className="bg-white/10 px-2 py-1 rounded">collect_mtf_snapshot.sh</code> on your server to generate snapshots.
        </p>
        <Button onClick={fetchLatestSnapshot} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }
  
  if (!snapshot) {
    return (
      <div className="glass-card p-8 rounded-xl border border-white/10 text-center" data-testid="mtf-analysis-empty">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">No Snapshots Found</h3>
        <p className="text-muted-foreground mb-4">
          Run <code className="bg-white/10 px-2 py-1 rounded">collect_mtf_snapshot.sh</code> to generate your first snapshot.
        </p>
      </div>
    );
  }
  
  const overall = snapshot.overall || {};
  const dataPeriod = snapshot.data_period || {};
  
  return (
    <div className="space-y-6" data-testid="mtf-analysis-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            MTF Performance Analysis
          </h2>
          <p className="text-sm text-muted-foreground">
            All Executed Trades (BUY + SELL | All Market Trends) • Snapshot: {snapshot.snapshot_time ? new Date(snapshot.snapshot_time).toLocaleString() : "Unknown"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchLatestSnapshot} variant="outline" size="sm" data-testid="refresh-snapshot">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleExport} 
            variant="outline" 
            size="sm" 
            disabled={exporting}
            data-testid="export-snapshot"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
      
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Trades"
          value={overall.trades || 0}
          subValue={`${dataPeriod.days || 0} days`}
          icon={Activity}
          color="text-blue-400"
        />
        <StatCard
          title="Win Rate"
          value={formatPercent(overall.win_rate)}
          subValue={`${overall.wins || 0}W / ${overall.losses || 0}L`}
          icon={Target}
          color={getWinRateColor(overall.win_rate)}
        />
        <StatCard
          title="Total PnL"
          value={formatPnL(overall.pnl)}
          subValue={`Avg: ${formatPnL(overall.avg_pnl)}/trade`}
          icon={overall.pnl >= 0 ? TrendingUp : TrendingDown}
          color={getPnLColor(overall.pnl)}
        />
        <StatCard
          title="Data Period"
          value={`${dataPeriod.days || 0} Days`}
          subValue={dataPeriod.start ? `${new Date(dataPeriod.start).toLocaleDateString()} - ${new Date(dataPeriod.end).toLocaleDateString()}` : "-"}
          icon={Clock}
          color="text-cyan-400"
        />
      </div>
      
      {/* Market Trend & Signal Type Breakdowns - 2 Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownTable title="By Market Trend" data={snapshot.by_market_trend} icon={Activity} />
        <BreakdownTable title="By Signal Type" data={snapshot.by_signal_type} icon={TrendingUp} />
      </div>
      
      {/* Core Breakdowns - 3 Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownTable title="By Momentum" data={snapshot.by_momentum} icon={TrendingUp} />
        <BreakdownTable title="By Range" data={snapshot.by_range} icon={Target} />
        <BreakdownTable title="By Hourly Trend" data={snapshot.by_hourly_trend} icon={Clock} />
      </div>
      
      {/* Guard Scenarios - Full Width */}
      <GuardScenariosTable data={snapshot.guard_scenarios} />
      
      {/* Combo Analysis */}
      <ComboAnalysisTable data={snapshot.combo_analysis} />
      
      {/* Additional Breakdowns - 3 Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownTable title="By Coin" data={snapshot.by_coin} icon={Coins} />
        <BreakdownTable title="By Session" data={snapshot.by_session} icon={Clock} />
        <BreakdownTable title="By Exit Reason" data={snapshot.by_exit_reason} icon={Target} />
      </div>
      
      {/* More Breakdowns - 3 Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownTable title="By Confidence" data={snapshot.by_confidence} icon={Target} />
        <BreakdownTable title="By RSI" data={snapshot.by_rsi} icon={Activity} />
        <BreakdownTable title="By Fear & Greed" data={snapshot.by_fear_greed} icon={AlertTriangle} />
      </div>
      
      {/* Individual Trades - Expandable */}
      <TradesTable snapshotId={snapshot._id} />
      
      {/* All Snapshots History */}
      <SnapshotsHistoryTable 
        onSelectSnapshot={fetchSnapshotById} 
        currentSnapshotId={snapshot._id} 
      />
    </div>
  );
};

export default MtfAnalysisTab;
